import React, { useEffect, useRef, useMemo } from "react";
import { Message, MessageWithStatus } from "../../types";
import { Timestamp } from "firebase/firestore";
import MessageBubble from "./MessageBubble";
import { CircularSkeleton } from "../common/LoadingSkeleton";

interface MessageListProps {
  messages: MessageWithStatus[];
  currentUserId?: string;
  otherUser?: {
    uid?: string;
    photoURL?: string | null;
    displayName?: string;
  };
  onRetryMessage?: (messageId: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  autoScroll?: boolean;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  currentUserId,
  otherUser,
  onRetryMessage,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  autoScroll = true,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const messageGroups = useMemo(() => {
    const groups: { [key: string]: Message[] } = {};

    messages.forEach((message) => {
      const date =
        message.timestamp instanceof Timestamp
          ? message.timestamp.toDate()
          : new Date(message.timestamp);
      const dateKey = date.toDateString();

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(message);
    });

    return groups;
  }, [messages]);

  useEffect(() => {
    if (autoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length, autoScroll]);

  const formatDateLabel = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      // Shorter format: "Dec 13" instead of full date
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  const shouldShowAvatar = (
    message: Message,
    index: number,
    messageGroup: Message[]
  ) => {
    if (message.senderId === currentUserId) return false;
    const nextMessage = messageGroup[index + 1];
    return !nextMessage || nextMessage.senderId === currentUserId;
  };

  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] py-12">
        <div className="text-center px-4 max-w-sm mx-auto">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-10 h-10 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h3 className="text-base font-medium text-gray-700 mb-1">No messages yet</h3>
          <p className="text-sm text-gray-500">Start the conversation by sending a message below</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={listRef} className="py-2 space-y-1">
      {/* Load more */}
      {hasMore && !loadingMore && (
        <div className="flex justify-center py-2">
          <button
            onClick={onLoadMore}
            className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1 rounded-full hover:bg-gray-100"
          >
            Load earlier messages
          </button>
        </div>
      )}

      {loadingMore && (
        <div className="flex justify-center py-2">
          <CircularSkeleton size="sm" />
        </div>
      )}

      {/* Messages grouped by date */}
      {Object.entries(messageGroups).map(([dateString, messageGroup]) => (
        <div key={dateString}>
          {/* Date label - minimal */}
          <div className="flex justify-center py-2">
            <span className="text-[11px] text-gray-400 bg-white/80 px-2 py-0.5 rounded">
              {formatDateLabel(dateString)}
            </span>
          </div>

          {/* Messages */}
          {messageGroup.map((message, index) => (
            <MessageBubble
              key={message.id || index}
              message={message}
              isMyMessage={message.senderId === currentUserId}
              showAvatar={shouldShowAvatar(message, index, messageGroup)}
              onRetry={onRetryMessage}
              otherUser={otherUser}
            />
          ))}
        </div>
      ))}

      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
